import React, { useState, useEffect, Suspense, useContext } from "react";

import { t } from "i18next";
import { Trans } from "react-i18next";
import { Link, Outlet, useNavigate } from "react-router-dom";

import Navbar from "../components/Navbars/Navbar";
import SideMenu from "../components/SideMenus/SideMenu";
// LABSTORE_ACCESS -- isLabstoreAuthorized
import { ROLES } from "../utils/constants";
import { isAuthorized } from "../utils/RolesHelpers";
import Footer from "../components/Footers/Footer";
import SimpleNotification from "../components/Notifications/SimpleNotification";
import { longDate } from "../utils/DateHelper";
import LegalSettingsAPI from "../API/legalSettings.api";
import { setItem } from "../utils/StorageHelpers";
import LoadingOverlay from "../components/Loaders/LoadingOverlay";
import { GLOBAL_ROUTES } from "../utils/RoutesConstant";
import { UserContext } from "../lib/MainContext";

const Admin = () => {
    const { user: currentUser, setUser } = useContext(UserContext);
    const [isLegalConfirmationLoading, setIsLegalConfirmationLoading] =
        useState(false);
    const navigate = useNavigate();
    const organization = currentUser?.organization;
    const [
        isLicenseExpirationMessageVisible,
        setIsLicenseExpirationMessageVisible,
    ] = useState();

    useEffect(() => {
        if (currentUser && isAccessRestricted())
            navigate(GLOBAL_ROUTES.LicenseExpired);
        if (currentUser?.organization)
            setIsLicenseExpirationMessageVisible(
                !currentUser?.organization?.isLicenseValid
            );
    }, [currentUser]);

    const isAccessRestricted = () => {
        const userOrganization = currentUser?.organization;
        return (
            !userOrganization?.isLicenseValid &&
            !userOrganization?.isLicenseInExtendedPeriod &&
            !currentUser.role === ROLES.superAdmin
        );
    };

    const isLegalSettingsAccepted = () => {
        if (
            !isAuthorized(
                [ROLES.support, ROLES.partnerAdmin],
                [currentUser.role]
            )
        )
            return true;

        return currentUser?.isLegalSettingsAccepted;
    };

    const confirmLegalSettings = async () => {
        setIsLegalConfirmationLoading(true);
        const result = await LegalSettingsAPI.confirm();

        if (result?.modified) {
            setItem("user", {
                ...currentUser,
                isLegalSettingsAccepted: true,
            });
            setUser({ ...currentUser, isLegalSettingsAccepted: true });
        }

        setIsLegalConfirmationLoading(false);
    };

    const renderLegalSettingsButton = () => {
        const buttonContent = isLegalConfirmationLoading ? (
            <>
                <span className="animate-spin mr-3 inline-flex">
                    <i className="fas fa-sync"></i>
                </span>
                <Trans i18nKey="requesting">Requesting...</Trans>
            </>
        ) : (
            t("Accept")
        );

        return (
            <button
                onClick={confirmLegalSettings}
                className={`secondary-green-btn ${
                    isLegalConfirmationLoading ? "disabled" : ""
                }`}
                disabled={isLegalConfirmationLoading}
            >
                {buttonContent}
            </button>
        );
    };

    const getLegalSettingsMessage = () => {
        const expiryDate = longDate(
            currentUser?.legalSettings?.confirmationExpiryDate || Date.now()
        );
        const termsAndConditions =
            getLegalSettingsLinkFor("termsAndConditions");

        const endUserAgreement = getLegalSettingsLinkFor("endUserAgreement");

        const html = t("legalSettingsNotification", {
            expiryDate,
        })
            .replace("{{termsandConditions}}", termsAndConditions)
            .replace("{{endUserAgreement}}", endUserAgreement);

        return (
            <p
                dangerouslySetInnerHTML={{
                    __html: ` <i class="fas fa-exclamation-triangle"></i> ${html}`,
                }}
            />
        );
    };

    const getLegalSettingsLinkFor = (key = "termsAndConditions") => {
        const links = currentUser?.legalSettings?.links;

        return `<a
                class="underline text-blue-600"
                href=${links?.[key] || "#"}
                target="_blank"
                rel="noreferrer"
            >
                ${t(key)}
            </a>`;
    };

    if (!currentUser) return <></>;
    return (
        <>
            <Navbar />
            <SideMenu />

            <div className="license-notification sticky top-0 z-98 pt-16 w-full">
                {isLicenseExpirationMessageVisible && (
                    <>
                        <SimpleNotification
                            message={
                                <>
                                    <Trans
                                        i18nKey="licenseExpiredNotification"
                                        values={{
                                            email: "supportNIR@buchi.com",
                                        }}
                                    >
                                        Your license has expired. Please contact
                                        us at
                                        <Link
                                            className="underline text-blue-600"
                                            to="#"
                                            onClick={(e) => {
                                                window.location =
                                                    "mailto:supportNIR@buchi.com";
                                                e.preventDefault();
                                            }}
                                        >
                                            supportNIR@buchi.com
                                        </Link>
                                        to renew your license.
                                    </Trans>
                                </>
                            }
                            className="main-content banner"
                            marginBottom=""
                        />
                        {organization?.isLicenseInExtendedPeriod && (
                            <div
                                onClick={() =>
                                    setIsLicenseExpirationMessageVisible(false)
                                }
                                className="mt-1 absolute top-16 right-3 icon-wrapper cursor-pointer"
                            >
                                <i className="fas fa-times" />
                            </div>
                        )}
                    </>
                )}

                {!isLegalSettingsAccepted() && (
                    <SimpleNotification
                        component={
                            <div className="text-xs w-full flex justify-between gap-1">
                                {getLegalSettingsMessage()}
                                <div className="flex items-start">
                                    {renderLegalSettingsButton()}
                                </div>
                            </div>
                        }
                        className="main-content banner"
                        marginBottom=""
                    />
                )}
            </div>
            {/* Main Content */}
            <main className="main-content expired-license" id="main-content">
                <Suspense
                    fallback={
                        <LoadingOverlay active>
                            <div className="suspense-fallback" />
                        </LoadingOverlay>
                    }
                >
                    <Outlet />
                </Suspense>
            </main>
            <Footer />
        </>
    );
};

export default Admin;
